import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

import { providerDashboard } from "@/lib/provider-payments.functions";
import { listProviderAudit } from "@/lib/external-bookings.functions";
import { PAYMENT_STATUS_LABEL, money } from "@/lib/provider-schemas";
import { downloadCsv } from "@/lib/csv-export";
import { ErrorBox, ProviderModuleShell, StatCard } from "@/components/admin/provider/ProviderModuleShell";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/provider-reconciliation")({
  head: () => ({
    meta: [
      { title: "Conciliación de proveedores — Administración" },
      { name: "description", content: "Diferencias de pago, cancelaciones, no shows y bitácora de cambios." },
      { property: "og:title", content: "Conciliación de proveedores — Administración · Mostrando México" },
      { property: "og:description", content: "Administración privada de proveedores por empresa." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  errorComponent: ({ error }) => <ErrorBox error={error as Error} />,
  notFoundComponent: () => <ErrorBox error={new Error("No encontrado")} />,
  component: () => (
    <ProviderModuleShell
      title="Conciliación"
      description="Reservas con diferencias entre costo y pagos, cancelaciones/no shows y bitácora de quién cambió costos, proveedores o estados."
    >
      <Body />
    </ProviderModuleShell>
  ),
});

function Body() {
  const { data } = useSuspenseQuery(
    queryOptions({ queryKey: ["admin", "provider-dashboard"], queryFn: () => providerDashboard() }),
  );
  const audit = useSuspenseQuery(
    queryOptions({
      queryKey: ["admin", "provider-audit"],
      queryFn: () => listProviderAudit({ data: {} }),
    }),
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Reservas con diferencia" value={data.differences.length} />
        <StatCard
          label="Canceladas"
          value={data.totals.cancelled.count}
          hint={money(data.totals.cancelled.amount)}
        />
        <StatCard label="No show" value={data.totals.noShow.count} hint={money(data.totals.noShow.amount)} />
        <StatCard label="Movimientos auditados" value={audit.data.entries.length} />
      </div>

      <section className="overflow-x-auto rounded-xl border bg-card">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="font-semibold">Diferencias entre costo y pagos</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              downloadCsv(
                "conciliacion-diferencias",
                [
                  { key: "service_date", label: "Fecha" },
                  { key: "tour_title", label: "Tour" },
                  { key: "provider_cost", label: "Costo" },
                  { key: "paid_amount", label: "Pagado" },
                  { key: "pending_amount", label: "Diferencia" },
                  { key: "cost_currency", label: "Moneda" },
                  { key: "payment_status", label: "Estado" },
                ],
                data.differences as never,
              )
            }
          >
            Exportar CSV
          </Button>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Servicio</th>
              <th className="px-3 py-2">Tour</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2 text-right">Costo</th>
              <th className="px-3 py-2 text-right">Pagado</th>
              <th className="px-3 py-2 text-right">Diferencia</th>
              <th className="px-3 py-2">Estado</th>
            </tr>
          </thead>
          <tbody>
            {data.differences.map((r: any) => (
              <tr key={r.id} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{r.service_date}</td>
                <td className="px-3 py-2 text-xs">{r.tour_title}</td>
                <td className="px-3 py-2 text-xs">
                  {r.customer_first_name} {r.customer_last_name}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.provider_cost, r.cost_currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.paid_amount, r.cost_currency)}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {money(r.pending_amount, r.cost_currency)}
                </td>
                <td className="px-3 py-2 text-xs">
                  {PAYMENT_STATUS_LABEL[r.payment_status as keyof typeof PAYMENT_STATUS_LABEL] ??
                    r.payment_status}
                </td>
              </tr>
            ))}
            {!data.differences.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Todo conciliado: no hay diferencias.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="overflow-x-auto rounded-xl border bg-card">
        <header className="border-b px-4 py-3">
          <h2 className="font-semibold">Bitácora de cambios</h2>
          <p className="text-xs text-muted-foreground">
            Quién cambió costos, proveedores, estados o registró pagos e importaciones.
          </p>
        </header>
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Usuario</th>
              <th className="px-3 py-2">Entidad</th>
              <th className="px-3 py-2">Acción</th>
              <th className="px-3 py-2">Campo</th>
              <th className="px-3 py-2">Antes</th>
              <th className="px-3 py-2">Después</th>
            </tr>
          </thead>
          <tbody>
            {audit.data.entries.map((e: any) => (
              <tr key={e.id} className="border-t text-xs">
                <td className="px-3 py-2 whitespace-nowrap">
                  {new Date(e.created_at).toLocaleString("es-MX")}
                </td>
                <td className="px-3 py-2">{e.actor_email ?? "—"}</td>
                <td className="px-3 py-2">{e.entity}</td>
                <td className="px-3 py-2">{e.action}</td>
                <td className="px-3 py-2">{e.field ?? "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{e.old_value ?? "—"}</td>
                <td className="px-3 py-2">{e.new_value ?? "—"}</td>
              </tr>
            ))}
            {!audit.data.entries.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  Sin movimientos registrados todavía.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
